<template>
	<div class="subviewOpts" style="align-items: unset;">
		<div class="comment-grid-container" :style="commentsHeightStyle">
			<div ref="commentsContainer" class="comment-grid-items comments" 
					 @scroll="checkScroll">
				<div v-if="hasNext && agenda.size > 0" class="loading-message">
					<sym-icon v-if="loading" name="refresh" class="uploadingIcon" />
					<sym-icon v-else name="arrow-up-heavy"/>
					{{ env.msg("comment.scrollOnTop") }}
				</div>
				<comment-bubble v-for="comment in comments" :comment="comment"
					 :users="allUsers" :key="comment.id" @reply="replyComment" />
			</div>
			<div class="comment-grid-form">
				<comment-form ref="commentForm" :edit="true" name="commentForm" :comment="form" @created="addComment" />
			</div>
			<span class="comment-grid-setting">
				<anchor-element tag="span" :chain="chain" style="margin-right: 4px;">
					<sym-icon :name="agenda.meta.muted || agenda.meta.disabled ? 'muted' : 'unmuted'" class="link-like-fi" @click="chain.toggle"/>
				</anchor-element>
			</span>
		</div>
		<boat-element v-if="chain.visible" :chain="chain" tag="div" class="menu-panel boat tail-top-right">
			<div v-if="agenda.meta.disabled" class="menu-panel-body">
				<a href="#" @click.stop.prevent="enable()" class="menu-item">
					<span class="mi mi-label">{{ env.msg("mute.enableNotifications") }}</span>
				</a>
			</div>
			<div v-else-if="agenda.meta.muted" class="menu-panel-body">
				<a href="#" @click.stop.prevent="unmute()" class="menu-item">
					<span class="mi mi-label">{{ env.msg("mute.cancel") }}</span>
				</a>
			</div>
			<div v-else class="menu-panel-body">
				<a href="#" @click.stop.prevent="mute(24*60*60*1000)" class="menu-item">
					<span class="mi mi-label">{{ env.msg("mute.tomorrow") }}</span>
				</a>
				<a href="#" @click.stop.prevent="mute(7*24*60*60*1000)" class="menu-item">
					<span class="mi mi-label">{{ env.msg("mute.week") }}</span>
				</a>
				<a href="#" @click.stop.prevent="mute(0)" class="menu-item">
					<span class="mi mi-label">{{ env.msg("mute.forever") }}</span>
				</a>
				<a href="#" @click.stop.prevent="disable()" class="menu-item">
					<span class="mi mi-label">{{ env.msg("mute.disableNotifications") }}</span>
				</a>
			</div>
		</boat-element>
	</div>
</template>
<script setup lang="ts">
import {ref, computed, onMounted, watch, watchEffect, nextTick} from 'vue';
import {injectEnv, userInRole} from "@fd/Environment";

import SymIcon from "@fd/helpers/SymIcon.vue";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import BoatElement from "@/ui/paneltree/BoatElement.vue";
import {createChain} from "@/ui/paneltree/PanelTree";
import CommentForm from "@/vue/note/CommentForm.vue";
import CommentBubble from "@/vue/note/CommentBubble.vue";

import type {Comment} from "@t/note/Comment";
import type {AgendaData} from "@t/agenda/Agenda";
import type {SimplifiedUser} from "@t/user/SimplifiedUser";
import {ContextKey, getIntValue, injectContext} from "@t/fd/AppContext.ts";
import {loadSourceType, SourceType} from "@t/meta/SourceType.ts";
import {UserRef} from "@t/util/CommonTypes.ts";
import {debounce} from "@/utils/Debounce.ts";


const props = defineProps<{
	srcType?: number,
	srcId?: string|number,
	endpoint?: string
}>();

const env = injectEnv();

const agenda = ref<AgendaData<Comment>>({ position: 0, total:0, size:0, items:[], meta: {muted: false} });
const AGENDA_SIZE = 10;
const loading = ref(false);

const allUsers = ref<SimplifiedUser[]>([]);
const commentsContainer = ref(null);
const commentForm = ref(null);

const chain = createChain({
	rule: {
		ah: 100, av: 100,
		bh: 100, bv: 0,
		x: 8, y: 0
	}
}, "mute-menu");

const ctx = injectContext();
const sourceType = computed<number>(() => props.srcType ?? getIntValue(ctx.value, ContextKey.SRC_TYPE));
const sourceId = computed<string|number>(() => props.srcId ?? getIntValue(ctx.value, ContextKey.SRC_ID));
const type = ref<SourceType>(null);

const commentsHeight = ref<number>(0);

const form = ref<Comment>({srcType: sourceType.value, srcId: String(sourceId.value), type: 'CLASSIC', content: ''});

function setEndpointId(endpoint: string, id: number | string):string {
	return endpoint.replace("{id}", id.toString());
}

watch(sourceType, async () => {
	if (!sourceType.value) return;
	if (type.value == null || type.value.id !== sourceType.value) {
		type.value = await loadSourceType(env, sourceType.value);
	}
}, { immediate: true });

const backendEndpoint = computed<string|null>(() => {
	if (props.endpoint) return props.endpoint;
	if (type.value && sourceId.value) {
		let ep = "api"+type.value.edit+"/comments";
		if (type.value.comments) ep = "api" + type.value.comments;
		return setEndpointId(ep, sourceId.value);
	}
	return null;
})

const commentsHeightStyle = computed<string>(() => {
	return 'max-height: '+(commentsHeight.value>0? (commentsHeight.value+"px;") : "65dvh;");
});

watchEffect(() => {
	if (backendEndpoint.value) {
		let url = env.net.urlParam(backendEndpoint.value, "meta", "true");
		url = env.net.urlParam(url, "position", 0);
		env.net.fetch(url)
			.then(response => response.json())
			.then(data => {
				agenda.value = data;
			})
			.then(() => {
				scrollBottom(true);
			})
			.catch((error) => {
				console.log("Error loading comments:" + error.message);
			});
	}
});

const hasNext = computed<boolean>(() => {
	return agenda.value.position + agenda.value.size < agenda.value.total;
});

const comments = computed<Comment[]>(() => {
	if (agenda.value.items) {
		return agenda.value.items.toReversed();
	} else return [];
});

function checkScroll(): void {
	if (commentsContainer.value.scrollTop == 0) {
		//console.log('User scroll top');
		if (hasNext.value) {
			loadOlderComments();
		}
	}
}


function loadOlderComments(): void {
	loading.value = true;
	let previousScrollHeight = commentsContainer.value.scrollHeight;
	let previousScrollTop = commentsContainer.value.scrollTop;
	agenda.value.position = agenda.value.position+AGENDA_SIZE;
	console.log('loading older comments from position: '+agenda.value.position);
	
	let url = backendEndpoint.value;
		url = env.net.urlParam(url, "position", agenda.value.position);
		url = env.net.urlParam(url, "size", AGENDA_SIZE);
		env.net.fetch(url)
			.then(response => response.json())
			.then(data => {
				loading.value = false;
				agenda.value.items.push(...data.items);
				nextTick(() => { 
					const heightDifference = commentsContainer.value.scrollHeight - previousScrollHeight;
					commentsContainer.value.scrollTop = previousScrollTop + heightDifference
				});
			})
			.catch((error) => {
				console.log("Error loading comments:" + error.message);
			});
} 

function mute(milis: number): void {
	env.net.fetch(backendEndpoint.value + "/mute?mutedTo=" + milis)
		.then(response => response.json())
		.then(data => {
			agenda.value.meta.muted = data.muted;
			chain.hide();
			//console.log("Get data:"+JSON.stringify(data));
		})
		.catch((error) => {
			console.log("Error muting comments with value:" + milis + " - " + error.message);
		});
}

function disable(): void {
	env.net.fetch(backendEndpoint.value + "/disable")
		.then(response => response.json())
		.then(data => {
			agenda.value.meta.disabled = data.disabled;
			chain.hide();
			//console.log("Get data:"+JSON.stringify(data));
		})
		.catch((error) => {
			console.log("Error disabling notifications - " + error.message);
		});
}

function enable(): void {
	env.net.fetch(backendEndpoint.value + "/enable")
		.then(response => response.json())
		.then(data => {
			agenda.value.meta.disabled = !data.enabled;
			chain.hide();
			//console.log("Get data:"+JSON.stringify(data));
		})
		.catch((error) => {
			console.log("Error disabling notifications - " + error.message);
		});
}

function unmute(): void {
	env.net.fetch(backendEndpoint.value + "/unmute")
		.then(response => response.json())
		.then(data => {
			agenda.value.meta.muted = data.muted;
			chain.hide();
			//console.log("Get data:"+JSON.stringify(data));
		})
		.catch((error) => {
			console.log("Error unmuting comments:" + error.message);
		});
}

function scrollBottom(fast: boolean) {
	nextTick(() => {
		//console.log("scroll bottom");
		if (commentsContainer.value) {
			commentsContainer.value.scrollTo({
				top: commentsContainer.value.scrollHeight, 
				behavior: fast?'instant':'smooth' 
			});
		}
	});
}

function addComment(comment: Comment) {
	agenda.value.items.unshift(comment);
	scrollBottom(false);
}

function replyComment(comment: Comment) {
	commentForm.value.replyComment(comment);
}

async function init():Promise<void> {
	allUsers.value = await env.users();
}

function countCommentsHeight(): void {
	const master = document.querySelector('.main-object') 
				|| document.querySelector('.object-content-inner');
		
	if (master) {
		commentsHeight.value = Math.round(master.clientHeight-38); 
		//console.log("count comments height:"+commentsHeight.value);
	}
}
const countCommentsHeightVar = debounce(countCommentsHeight, 100);


//on create init and load comments
onMounted(() => {
	init();
	countCommentsHeightVar();
});


</script>

<style>

.comments {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	row-gap: 18px;
	overflow: hidden;
	scrollbar-gutter: stable;
	scrollbar-width: thin;
}

.comments:hover {
  overflow-y: auto;
}

.loading-message {
	padding: 2px;
	opacity:0.5;
}

.comment-grid-container {
	display: grid;
	width: 100%;
	max-width: 60rem;
	grid-template-columns: 1fr 24px;
	gap: 8px;
	grid-template-areas:
    "items setting"
    "form setting";
}

.comment-grid-items {
  grid-area: items;
}

.comment-grid-setting {
  grid-area: setting;
}

.comment-grid-form {
  grid-area: form;
}

</style>